import React, { useState } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../../convex/_generated/api';
import { Button } from '../../../../catalyst-components/button';
import { Spinner } from '../../../../catalyst-components/spinner';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../../catalyst-components/table';
import { Badge } from '../../../../catalyst-components/badge';
import {
    ArrowLeftIcon,
    ClipboardDocumentListIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from '@heroicons/react/24/outline';

const PAGE_SIZE = 50;

const ACTION_COLORS: Record<string, string> = {
    create: 'green',
    update: 'blue',
    delete: 'red',
    toggle: 'yellow',
    reorder: 'purple',
    seed: 'cyan',
    grant: 'orange',
};

function formatTimestamp(ts: number): string {
    return new Date(ts).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

function formatEntityType(type: string): string {
    return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

function formatValue(val: unknown): string {
    if (val === null || val === undefined) return '(empty)';
    if (typeof val === 'string') {
        if (val.length > 80) return val.slice(0, 80) + '...';
        return val;
    }
    if (typeof val === 'boolean') return val ? 'true' : 'false';
    if (typeof val === 'number') return String(val);
    return JSON.stringify(val).slice(0, 80);
}

const DetailsCell = ({ details }: { details?: string }) => {
    if (!details) return <span className="text-muted-foreground">—</span>;

    let parsed: Record<string, unknown> | null = null;
    try {
        parsed = JSON.parse(details);
    } catch {
        return <span className="text-xs text-muted-foreground">Has details</span>;
    }

    const entries = Object.entries(parsed!);
    if (entries.length === 0) return <span className="text-muted-foreground">—</span>;

    return (
        <span className="text-xs text-primary">
            {entries.length} field{entries.length !== 1 ? 's' : ''} changed
        </span>
    );
};

export const AuditLog: React.FC = () => {
    const navigate = useNavigate();
    const [selectedType, setSelectedType] = useState<string>('');
    const [cursor, setCursor] = useState<string | undefined>(undefined);
    const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([]);
    const [pageNum, setPageNum] = useState(1);

    const entityTypes = useQuery(api.audit_log.getEntityTypes);
    const result = useQuery(
        api.audit_log.getRecentEntries,
        selectedType
            ? { entity_type: selectedType, limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) }
            : { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) }
    );

    const handleFilterChange = (type: string) => {
        setSelectedType(type);
        setCursor(undefined);
        setCursorHistory([]);
        setPageNum(1);
    };

    const handleNextPage = () => {
        if (result?.nextCursor) {
            setCursorHistory((prev) => [...prev, cursor]);
            setCursor(result.nextCursor);
            setPageNum((p) => p + 1);
        }
    };

    const handlePrevPage = () => {
        if (cursorHistory.length > 0) {
            const prev = [...cursorHistory];
            const prevCursor = prev.pop();
            setCursorHistory(prev);
            setCursor(prevCursor);
            setPageNum((p) => p - 1);
        }
    };

    if (!result) {
        return (
            <div className="min-h-screen bg-background pt-24">
                <div className="border-b border-border/50 bg-gradient-to-r from-green-50 to-green-100/50 px-6 py-8">
                    <div>
                        <div className="flex items-center gap-4 mb-4">
                            <Button color="secondary" className="rounded-full" onClick={() => navigate('/admin')}>
                                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                                Back
                            </Button>
                        </div>
                        <h1 className="text-3xl font-semibold text-foreground">Audit Log</h1>
                    </div>
                </div>
                <div className="flex justify-center items-center h-64">
                    <Spinner className="h-12 w-12" />
                </div>
            </div>
        );
    }

    const entries = result.entries;

    return (
        <div className="min-h-screen bg-background pt-24">
            {/* Header */}
            <div className="border-b border-border/50 bg-gradient-to-r from-green-50 to-green-100/50 px-6 py-8">
                <div>
                    <div className="flex items-center gap-4 mb-4">
                        <Button color="secondary" className="rounded-full" onClick={() => navigate('/admin')}>
                            <ArrowLeftIcon className="h-4 w-4 mr-2" />
                            Back
                        </Button>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-white border-2 border-green-200">
                            <ClipboardDocumentListIcon className="h-8 w-8 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-semibold text-foreground mb-1">Audit Log</h1>
                            <p className="text-base text-muted-foreground">
                                Every admin action recorded
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-6 py-8">
                <div className="space-y-6">
                    {/* Filters */}
                    <div className="flex flex-wrap items-center gap-3">
                        <Button
                            color={selectedType === '' ? 'primary' : 'secondary'}
                            className="rounded-full text-sm"
                            onClick={() => handleFilterChange('')}
                        >
                            All
                        </Button>
                        {entityTypes?.map((type) => (
                            <Button
                                key={type}
                                color={selectedType === type ? 'primary' : 'secondary'}
                                className="rounded-full text-sm"
                                onClick={() => handleFilterChange(type)}
                            >
                                {formatEntityType(type)}
                            </Button>
                        ))}
                        <div className="ml-auto px-4 py-2 bg-muted/30 border border-border/50 rounded-full">
                            <span className="text-sm text-muted-foreground">Page </span>
                            <span className="font-semibold text-foreground">{pageNum}</span>
                            <span className="text-sm text-muted-foreground"> ({entries.length} entries)</span>
                        </div>
                    </div>

                    {/* Log Table */}
                    <div className="border-2 border-border/50 rounded-xl overflow-hidden bg-card">
                        {entries.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                                {pageNum === 1
                                    ? 'No audit log entries yet. Actions will appear here as admins make changes.'
                                    : 'No more entries.'}
                            </div>
                        ) : (
                            <Table>
                                <TableHead>
                                    <TableRow className="bg-muted/30">
                                        <TableHeader>Time</TableHeader>
                                        <TableHeader>Admin</TableHeader>
                                        <TableHeader>Action</TableHeader>
                                        <TableHeader>Entity</TableHeader>
                                        <TableHeader>Description</TableHeader>
                                        <TableHeader>Details</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {entries.map((entry: any) => (
                                        <TableRow
                                            key={entry._id}
                                            className="hover:bg-muted/20 cursor-pointer"
                                            onClick={() => navigate(`/admin/audit-log/${entry._id}`)}
                                        >
                                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                                {formatTimestamp(entry.timestamp)}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {entry.admin_email}
                                            </TableCell>
                                            <TableCell>
                                                <Badge color={(ACTION_COLORS[entry.action] || 'zinc') as any}>
                                                    {entry.action}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-sm font-medium">
                                                {formatEntityType(entry.entity_type)}
                                            </TableCell>
                                            <TableCell className="text-sm text-foreground max-w-xs truncate">
                                                {entry.description}
                                            </TableCell>
                                            <TableCell>
                                                <DetailsCell details={entry.details} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    <div className="flex items-center justify-between">
                        <Button
                            color="secondary"
                            className="rounded-full text-sm"
                            onClick={handlePrevPage}
                            disabled={pageNum === 1}
                        >
                            <ChevronLeftIcon className="h-4 w-4 mr-1" />
                            Previous
                        </Button>
                        <span className="text-sm text-muted-foreground">Page {pageNum}</span>
                        <Button
                            color="secondary"
                            className="rounded-full text-sm"
                            onClick={handleNextPage}
                            disabled={result.isDone}
                        >
                            Next
                            <ChevronRightIcon className="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
